// src/components/Control/robot/voice.ts
// Голос с сайта (нода audio на Pi), один на страницу — его используют кнопка
// микрофона в шапке, голосовой слой и экран «Няня»:
//  - команда: микрофон браузера -> robot/<id>/audio/voice {"mode": "command", "seq", "pcm", "end"},
//    робот распознаёт (Vosk) и выполняет. Звук копится в браузере и уходит только
//    по «Готово», поэтому «Отмена» действительно ничего не отправляет;
//    либо распознаёт браузер, а роботу уходит текст robot/<id>/audio/cmd {"cmd": "text"};
//  - «говорить через робота»: голос сразу в динамик робота (mode "speak");
//  - результат — события voice_command / voice_text / error в robot/<id>/audio/event.
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { Store } from './store';
import { EventsStore } from './events';
import { MicCapture, browserRecognizer, int16ToBase64, PCM_RATE } from '../../../audio/pcm';
import { putJson } from '../../../hooks/useZenohSubscription';
import { loadPref, savePref } from '../../../types/features';

export type VoicePhase = 'idle' | 'listening' | 'recognized' | 'confirm' | 'executing' | 'success' | 'error' | 'cancelled';
export type Recognizer = 'robot' | 'browser';

export interface VoiceState {
  phase: VoicePhase;
  speaking: boolean;          // «говорить через робота» включено
  level: number;              // уровень микрофона 0..1
  heard: string | null;       // распознанный текст
  message: string | null;     // итог / ошибка / вопрос
  recognizer: Recognizer;
}

// Самая длинная команда, с
const MAX_COMMAND_S = 12;
// Сколько ждать ответа робота после отправки, мс
const RESULT_TIMEOUT_MS = 8000;
// Ниже этой уверенности браузерного распознавания команду движения не выполняем без подтверждения
const MIN_CONFIDENCE = 0.6;
const MOTION_WORDS = /(вперёд|вперед|назад|налево|направо|влево|вправо|поезжай|едь|езжай|двигай|следуй|подъезжай|осмотри|объед)/i;

export const VOICE_EXAMPLES = ['вперёд', 'назад', 'налево', 'направо', 'стоп', 'скажи привет', 'что видишь',
  'что на карте', 'следуй за мной', 'осмотри комнату', 'включи режим няни', 'выключи няню', 'громче', 'тише',
  'сохрани карту', 'подключи колонку', 'грач, включи свет', 'ярче', 'включи белый на полную', 'выключи вторую ленту'];

export class VoiceController {
  readonly store = new Store<VoiceState>({
    phase: 'idle', speaking: false, level: 0, heard: null, message: null,
    recognizer: loadPref<Recognizer>('voice.recognizer', 'robot'),
  });

  private session: zenoh.Session | null = null;
  private robotId = '';
  private mic: MicCapture | null = null;
  private rec: any = null;
  private buffer: Int16Array[] = [];
  private seq = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private resultTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubEvents: (() => void) | null = null;
  // Перехват команд интерфейсом («включи камеру 2», «открой карту»); true — обработано
  localIntent: (text: string) => boolean = () => false;

  constructor(private events: EventsStore) {}

  setTarget(session: zenoh.Session | null, robotId: string) {
    this.session = session;
    this.robotId = robotId;
  }

  private get prefix() {
    return `robot/${this.robotId}/audio`;
  }

  setRecognizer(r: Recognizer) {
    savePref('voice.recognizer', r);
    this.store.set({ recognizer: r });
  }

  private clearTimers() {
    if (this.timer) clearTimeout(this.timer);
    if (this.resultTimer) clearTimeout(this.resultTimer);
    this.timer = null;
    this.resultTimer = null;
  }

  private fail(message: string) {
    this.clearTimers();
    this.store.set({ phase: 'error', message, level: 0 });
  }

  // Ждём ответа робота на команду
  private awaitResult() {
    this.store.set({ phase: 'executing', message: null });
    const since = Date.now();
    this.unsubEvents?.();
    this.unsubEvents = this.events.subscribe(() => {
      const ev = this.events.get().events.find(e => e.source === 'voice' && e.at >= since);
      if (!ev) return;
      this.unsubEvents?.();
      this.unsubEvents = null;
      if (this.resultTimer) clearTimeout(this.resultTimer);
      if (ev.kind === 'voice_command') {
        this.store.set({ phase: 'success', heard: ev.heard || this.store.get().heard, message: ev.text });
      } else if (ev.kind === 'error') {
        this.store.set({ phase: 'error', message: ev.text });
      } else {
        // voice_text: робот услышал, но команды не нашёл
        this.store.set({ phase: 'error', heard: ev.heard ?? this.store.get().heard, message: ev.text });
      }
    });
    this.resultTimer = setTimeout(() => {
      this.unsubEvents?.();
      this.unsubEvents = null;
      if (this.store.get().phase === 'executing') this.store.set({ phase: 'error', message: 'Робот не ответил' });
    }, RESULT_TIMEOUT_MS);
  }

  // Текстовая команда (набрана, выбрана из примеров или распознана браузером)
  sendText(text: string, source = 'text', confidence = 1) {
    const value = text.trim();
    if (!value) return;
    this.store.set({ heard: value, phase: 'recognized', message: null });
    if (this.localIntent(value)) {
      this.store.set({ phase: 'success', message: 'Выполнено' });
      return;
    }
    if (confidence < MIN_CONFIDENCE && MOTION_WORDS.test(value)) {
      this.store.set({ phase: 'confirm', message: `Я не уверен, что понял команду. Выполнить «${value}»?` });
      return;
    }
    this.dispatch(value, source);
  }

  confirm() {
    const heard = this.store.get().heard;
    if (heard) this.dispatch(heard, 'voice-confirmed');
  }

  private dispatch(value: string, source: string) {
    if (!this.session) return this.fail('Нет подключения к роботу');
    putJson(this.session, `${this.prefix}/cmd`, { cmd: 'text', text: value, source });
    this.awaitResult();
  }

  async startCommand() {
    const st = this.store.get();
    if (st.phase === 'listening' || st.speaking) return;
    this.clearTimers();
    this.unsubEvents?.();
    this.store.set({ phase: 'listening', heard: null, message: null, level: 0 });
    if (!this.session) return this.fail('Нет подключения к роботу');

    if (st.recognizer === 'browser') {
      const rec = browserRecognizer();
      if (!rec) return this.fail('Браузер не умеет распознавать речь — выберите «на роботе» в настройках голоса');
      let got = false;
      rec.onresult = (e: any) => {
        got = true;
        const alt = e.results?.[0]?.[0];
        const heard = alt?.transcript ?? '';
        const conf = typeof alt?.confidence === 'number' && alt.confidence > 0 ? alt.confidence : 1;
        this.rec = null;
        this.sendText(heard, 'browser', conf);
      };
      rec.onerror = (e: any) => {
        if (e.error === 'aborted') return;
        got = true;
        this.fail(e.error === 'no-speech' ? 'Ничего не расслышал' : `Распознавание: ${e.error}`);
      };
      rec.onend = () => {
        this.rec = null;
        if (!got && this.store.get().phase === 'listening') this.fail('Ничего не расслышал');
      };
      this.rec = rec;
      rec.start();
      return;
    }

    if (!MicCapture.supported()) return this.fail('Микрофон доступен только на https:// или localhost');
    this.buffer = [];
    const mic = new MicCapture(pcm => this.buffer.push(pcm), rms => this.store.set({ level: rms }));
    this.mic = mic;
    try {
      await mic.start();
    } catch (err) {
      this.mic = null;
      return this.fail(`Нет доступа к микрофону: ${err instanceof Error ? err.message : String(err)}`);
    }
    if (this.mic !== mic) {
      mic.stop(); // отменили, пока браузер спрашивал разрешение
      return;
    }
    // Тайм-аут: слишком долгая команда — отправляем, что есть
    this.timer = setTimeout(() => this.finish(), MAX_COMMAND_S * 1000);
  }

  // «Готово»: отправить сказанное
  finish() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.rec) {
      this.rec.stop(); // результат придёт в onresult
      return;
    }
    const mic = this.mic;
    if (!mic) return;
    this.mic = null;
    const tail = mic.stop();
    if (tail.length) this.buffer.push(tail);
    const chunks = this.buffer;
    this.buffer = [];
    const total = chunks.reduce((n, c) => n + c.length, 0);
    this.store.set({ level: 0 });
    if (total < PCM_RATE * 0.3) return this.fail('Слишком коротко — скажите команду ещё раз');
    if (!this.session) return this.fail('Нет подключения к роботу');
    chunks.forEach((pcm, i) => {
      const msg = { mode: 'command', seq: i, pcm: int16ToBase64(pcm), end: i === chunks.length - 1 };
      this.session!.put(`${this.prefix}/voice`, JSON.stringify(msg)).catch(err => console.warn('voice put failed', err));
    });
    this.store.set({ phase: 'recognized', heard: null, message: 'Распознаю на роботе…' });
    this.awaitResult();
  }

  // «Отмена»: ничего не отправлять
  cancel() {
    this.clearTimers();
    this.unsubEvents?.();
    this.unsubEvents = null;
    if (this.rec) {
      this.rec.abort?.();
      this.rec = null;
    }
    if (this.mic) {
      this.mic.stop();
      this.mic = null;
    }
    this.buffer = [];
    this.store.set({ phase: 'cancelled', level: 0, message: 'Отменено' });
  }

  reset() {
    if (this.mic || this.rec) this.cancel();
    this.store.set({ phase: 'idle', heard: null, message: null, level: 0 });
  }

  // «Говорить через робота»: голос сразу в динамик робота, пока включено
  async startSpeak() {
    const st = this.store.get();
    if (st.speaking || st.phase === 'listening' || !this.session) return;
    if (!MicCapture.supported()) throw new Error('Микрофон доступен только на https:// или localhost');
    this.seq = 0;
    const mic = new MicCapture(pcm => this.sendSpeak(pcm, false), rms => this.store.set({ level: rms }));
    this.mic = mic;
    this.store.set({ speaking: true });
    try {
      await mic.start();
    } catch (err) {
      this.mic = null;
      this.store.set({ speaking: false, level: 0 });
      throw err;
    }
    if (this.mic !== mic) mic.stop();
  }

  stopSpeak() {
    const mic = this.mic;
    if (!this.store.get().speaking) return;
    this.mic = null;
    if (mic) this.sendSpeak(mic.stop(), true);
    this.store.set({ speaking: false, level: 0 });
  }

  private sendSpeak(pcm: Int16Array, end: boolean) {
    if (!this.session) return;
    const msg = { mode: 'speak', seq: this.seq++, pcm: int16ToBase64(pcm), end };
    this.session.put(`${this.prefix}/voice`, JSON.stringify(msg)).catch(err => console.warn('voice put failed', err));
  }

  dispose() {
    this.cancel();
    this.stopSpeak();
  }
}
